import { getPlayersDb } from "@/utils/db/playersDb";

const STORE_NAME = "playersPool";
const CACHE_DURATION_DAYS = 6;
const CACHE_DURATION_MS = CACHE_DURATION_DAYS * 24 * 60 * 60 * 1000;

/**
 * Récupère le pool de joueurs depuis le cache local ou l'API
 * @param {number} championshipId - L'ID du championnat
 * @param {Function} fetchPlayersPool - Fonction pour récupérer les joueurs depuis l'API
 * @returns {Promise<Map<string, {firstName: string, lastName: string, position: number}>>} Map des joueurs par ID
 */
const getPlayersPool = async (championshipId, fetchPlayersPool) => {
  const localPool = await getLocalPlayersPool(championshipId);

  if (localPool) {
    return localPool;
  }

  try {
    const poolData = await fetchPlayersPool(championshipId);
    const playersMap = buildPlayersMap(poolData.poolPlayers);
    await setLocalPlayersPool(championshipId, playersMap);
    return playersMap;
  } catch (error) {
    console.error(`Impossible de récupérer le pool de joueurs pour le championnat ${championshipId}`, error);
    return new Map();
  }
};

/**
 * Construit une Map des joueurs à partir du tableau poolPlayers
 * @param {Array} poolPlayers - Tableau des joueurs
 * @returns {Map<string, {firstName: string, lastName: string, position: number}>}
 */
const buildPlayersMap = (poolPlayers) => {
  const playersMap = new Map();

  if (! poolPlayers || ! Array.isArray(poolPlayers)) {
    return playersMap;
  }

  poolPlayers.forEach((player) => {
    playersMap.set(player.id, {
      firstName: player.firstName,
      lastName: player.lastName,
      position: player.position,
    });
  });

  return playersMap;
};

/**
 * Récupère le pool de joueurs depuis IndexedDB
 * @param {number} championshipId
 * @returns {Promise<Map|null>}
 */
const getLocalPlayersPool = async (championshipId) => {
  const db = await getPlayersDb();

  const cached = await db.get(STORE_NAME, championshipId);

  if (cached) {
    const currentTime = Date.now();
    const storedTime = cached.updatedAt;
    const differenceInMs = currentTime - storedTime;

    if (differenceInMs < CACHE_DURATION_MS) {
      return new Map(cached.data);
    }
  }

  return null;
};

/**
 * Sauvegarde le pool de joueurs dans IndexedDB
 * @param {number} championshipId
 * @param {Map} playersMap
 */
const setLocalPlayersPool = async (championshipId, playersMap) => {
  const db = await getPlayersDb();

  await db.put(STORE_NAME, {
    data: Array.from(playersMap.entries()),
    updatedAt: Date.now(),
  }, championshipId);
};

/**
 * Récupère les infos d'un joueur (nom, poste) depuis la Map
 * @param {Map} playersPool - Map des joueurs
 * @param {string} playerId - ID du joueur
 * @returns {{firstName: string, lastName: string, position: number}|null}
 */
const getPlayerFromPool = (playersPool, playerId) => {
  return playersPool.get(playerId) || null;
};

export { getPlayersPool, getPlayerFromPool };